import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStatus } from '../hooks/useAuth.js';
import { useTheme } from '../contexts/ThemeContext.js';
import { Logo } from '../components/ui/Logo.js';
import { Icon } from '../components/ui/Icon.js';
import { Chip } from '../components/ui/Chip.js';
import { Placeholder, type PlaceholderTone } from '../components/ui/Placeholder.js';
import { SectionTitle } from '../components/ui/SectionTitle.js';

type IconNames = Parameters<typeof Icon>[0]['name'];

const MEALS: Array<{ day: string; b: string; l: string; d: string; tone: PlaceholderTone }> = [
  { day: 'Mon', b: 'Steel-cut oats',    l: 'Lentil bowl',   d: 'Roast chicken',     tone: 'warm'  },
  { day: 'Tue', b: 'Yogurt + berries',  l: 'Caesar salad',  d: 'Salmon, greens',    tone: 'cool'  },
  { day: 'Wed', b: 'Avocado toast',     l: 'Quinoa bowl',   d: 'Tofu stir-fry',     tone: 'leaf'  },
  { day: 'Thu', b: 'Chia pudding',      l: 'Turkey wrap',   d: 'Pasta primavera',   tone: 'grain' },
  { day: 'Fri', b: 'Frittata',          l: 'Soup + bread',  d: 'Sheet-pan tacos',   tone: 'spice' },
  { day: 'Sat', b: 'Pancakes',          l: 'BLT salad',     d: 'Margherita pizza',  tone: 'warm'  },
  { day: 'Sun', b: 'Shakshuka',         l: 'Grain bowl',    d: 'Sunday roast',      tone: 'leaf'  },
];

function AnimatedGrid({ compact = false }: { compact?: boolean }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (step >= MEALS.length * 3) {
      const t = setTimeout(() => setStep(0), 4000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStep(s => s + 1), step === 0 ? 600 : 180);
    return () => clearTimeout(t);
  }, [step]);

  const filled = (i: number, slot: number) => step > i * 3 + slot;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--line-soft)',
      borderRadius: 'var(--r-lg)',
      padding: compact ? 14 : 22,
      boxShadow: 'var(--shadow-pop)',
      width: '100%',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: compact ? 12 : 18 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 4 }}>
            This week's plan
          </div>
          <div className="h-serif" style={{ fontSize: compact ? 16 : 20, color: 'var(--ink)' }}>
            Apr 27 – May 3
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, fontWeight: 600,
          padding: '5px 10px',
          background: 'var(--accent-soft)', color: 'var(--accent)',
          borderRadius: 'var(--r-pill)',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--accent)', animation: 'pp-pulse 1.4s ease-in-out infinite' }} />
          Generating
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(3, 1fr)', gap: compact ? 6 : 9, alignItems: 'stretch' }}>
        <div />
        {['Breakfast', 'Lunch', 'Dinner'].map(s => (
          <div key={s} style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', paddingLeft: 4, paddingBottom: 2 }}>{s}</div>
        ))}
        {MEALS.map((m, i) => (
          <Row key={m.day} m={m} i={i} filled={filled} compact={compact} />
        ))}
      </div>

      <div style={{
        marginTop: compact ? 12 : 18,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingTop: compact ? 12 : 16,
        borderTop: '1px solid var(--line-soft)',
        fontSize: 11.5, color: 'var(--ink-3)',
      }}>
        <span>21 meals · this week's plan</span>
        <span style={{ color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
          $187 / $200 <Icon name="check" size={12} />
        </span>
      </div>
    </div>
  );
}

interface RowProps {
  m: typeof MEALS[number];
  i: number;
  filled: (i: number, slot: number) => boolean;
  compact: boolean;
}
function Row({ m, i, filled, compact }: RowProps) {
  return (
    <>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: compact ? 13 : 15,
        fontWeight: 500,
        color: 'var(--ink-2)',
        alignSelf: 'center',
        padding: '0 8px 0 0',
      }}>{m.day}</div>
      {[m.b, m.l, m.d].map((meal, slot) => {
        const on = filled(i, slot);
        return (
          <div key={slot} style={{
            position: 'relative',
            height: compact ? 38 : 44,
            borderRadius: 'var(--r-sm, 10px)',
            background: on ? 'var(--surface-2)' : 'transparent',
            border: on ? '1px solid var(--line-soft)' : '1px dashed var(--line-soft)',
            padding: compact ? '5px 8px' : '7px 10px',
            display: 'flex', alignItems: 'center', gap: 8,
            overflow: 'hidden',
            transform: on ? 'scale(1)' : 'scale(.96)',
            opacity: on ? 1 : 0.4,
            transition: 'all .35s cubic-bezier(.2,.8,.2,1)',
          }}>
            {on && (
              <>
                <div className={`ph ph-${m.tone}`} style={{
                  width: compact ? 24 : 30, height: compact ? 24 : 30,
                  borderRadius: 6, flexShrink: 0,
                }} />
                <span style={{
                  fontSize: compact ? 11 : 12,
                  fontWeight: 500,
                  color: 'var(--ink-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>{meal}</span>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

const trialPillStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '6px 14px',
  background: 'var(--accent-soft)', color: 'var(--accent)',
  borderRadius: 'var(--r-pill)',
  fontSize: 12, fontWeight: 600, letterSpacing: 0.2,
};

function TrialPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={trialPillStyle}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--accent)' }} />
      {children}
    </span>
  );
}

const STEPS: Array<{ n: string; t: string; d: string; icon: IconNames }> = [
  { n: '01', t: 'Set your taste',     d: "Family size, dietary needs, cuisines you love, your budget. Five minutes, once.", icon: 'user' },
  { n: '02', t: 'We build your week', d: '21 meals tailored to your budget, schedule, and skill level — every Sunday at 6am.',          icon: 'sparkle' },
  { n: '03', t: 'Cook, swap, repeat', d: "Hate Tuesday's dinner? One tap to swap. Your shopping list updates instantly.",                icon: 'refresh' },
];

const SCIENCE = [
  { stat: '27%',    label: 'less food waste',    body: 'Households that plan throw away significantly less food.',                   src: 'USDA · 2022' },
  { stat: '$1,500', label: 'saved per year',     body: 'Planned shopping cuts impulse buys for the average family of four.',         src: 'J. Nutrition Edu · 2021' },
  { stat: '35%',    label: 'less dinner stress', body: 'Parents report lower stress and more family meals eaten together.',          src: 'AHA · 2023' },
];

const SMALL_FEATURES: Array<{ t: string; d: string; tone: PlaceholderTone; icon: IconNames }> = [
  { t: 'Shop only what you need', d: 'Tell us what you already have on hand. Your list trims duplicates so nothing goes to waste.', tone: 'leaf',  icon: 'cart' },
  { t: 'Every diet, honored',   d: 'Keto, vegan, gluten-free, halal, kosher — preferences baked into every meal.', tone: 'spice', icon: 'shield' },
  { t: 'Swap with one tap',     d: "Don't like Tuesday's dinner? Swap it. Your shopping list rebuilds instantly.", tone: 'cool',  icon: 'swap'   },
  { t: 'Family roster',         d: 'Different ages, different needs. We balance them in one plan.',                tone: 'grain', icon: 'family' },
];

const TESTIMONIALS = [
  { name: 'Sarah M.',           loc: 'Austin, TX',  color: '#7dd3a8', quote: 'I used to spend 45 minutes every Sunday figuring out what to cook. Now I press a button and I\'m done. We eat healthier and I spend $80 less every week.', days: 'Member · 14 mo' },
  { name: 'James & Priya T.',   loc: 'Seattle, WA', color: '#7faed4', quote: 'Two kids with completely different food allergies. Platterplan handles it perfectly — every suggestion works for our whole family without me checking each recipe myself.', days: 'Member · 8 mo' },
  { name: 'David K.',           loc: 'Chicago, IL', color: '#b394d4', quote: 'I was skeptical about AI meal planning, but the suggestions are genuinely good. It learned what we actually like after the first week and I look forward to seeing the new plan every Monday.', days: 'Member · 22 mo' },
];

const PLAN_FEATURES = [
  'Unlimited weekly meal plans',
  'Smart, on-hand-aware shopping lists',
  'Every dietary restriction supported',
  'Cuisine preference matching',
  'Family-size & budget planning',
  'One-tap meal swapping',
  'Shareable shopping lists',
  'Cook mode with hands-free timers',
];

export function LandingPage() {
  const { data: profile, isLoading } = useAuthStatus();
  const { darkMode, toggleDark } = useTheme();
  const navigate = useNavigate();

  if (!isLoading && profile) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div style={{ background: 'var(--bg-deep)', color: 'var(--ink)', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* ── Marketing nav ─────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-50"
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--line-soft)',
          background: 'color-mix(in oklab, var(--bg) 92%, transparent)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex" style={{ gap: 28, fontSize: 13.5, fontWeight: 500, color: 'var(--ink-3)' }}>
            <a href="#how"          style={{ color: 'inherit', textDecoration: 'none' }}>How it works</a>
            <a href="#features"     style={{ color: 'inherit', textDecoration: 'none' }}>Features</a>
            <a href="#pricing"      style={{ color: 'inherit', textDecoration: 'none' }}>Pricing</a>
            <a href="#testimonials" style={{ color: 'inherit', textDecoration: 'none' }}>Stories</a>
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={toggleDark}
              title={darkMode ? 'Light mode' : 'Dark mode'}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                width: 36, height: 36,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent',
                color: 'var(--ink-3)',
                border: '1px solid var(--line-soft)',
                borderRadius: 'var(--r-pill)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'color .15s, border-color .15s, background .15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--ink)';
                e.currentTarget.style.background = 'var(--surface-2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--ink-3)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon name={darkMode ? 'sun' : 'moon'} size={15} />
            </button>
            <Link to="/login" style={{ color: 'var(--ink-2)', fontSize: 13.5, fontWeight: 500, textDecoration: 'none', padding: '6px 10px' }}>
              Sign in
            </Link>
            <Link to="/signup" className="btn btn-primary" style={{ padding: '10px 18px', fontSize: 13.5, textDecoration: 'none' }}>
              Start free trial
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative" style={{ padding: '64px 24px 48px' }}>
        <div style={{
          position: 'absolute', top: -120, right: -80, width: 600, height: 600,
          background: 'radial-gradient(circle, var(--accent-glow) 0%, transparent 60%)',
          pointerEvents: 'none', opacity: 0.6,
        }} />
        <div className="max-w-6xl mx-auto grid gap-10 md:gap-14 items-center" style={{ gridTemplateColumns: '1fr', position: 'relative' }}>
          <div className="md:grid md:grid-cols-[1.05fr_1fr] md:gap-14 md:items-center">
            <div style={{ position: 'relative' }}>
              <TrialPill>30-day free trial · No card</TrialPill>
              <h1 className="h-display" style={{ fontSize: 'clamp(48px, 9vw, 84px)', lineHeight: 0.96, margin: '20px 0 24px', color: 'var(--ink)' }}>
                Dinner,<br />
                <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>solved.</span>
              </h1>
              <p style={{ fontSize: 'clamp(15px, 1.6vw, 19px)', lineHeight: 1.55, color: 'var(--ink-2)', maxWidth: 480, margin: '0 0 32px' }}>
                Platterplan builds a personalized weekly meal plan for your family — based on your tastes, your budget,
                and your weekly budget. <strong style={{ color: 'var(--ink)' }}>Every week, automatically.</strong>
              </p>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <Link to="/signup" className="btn btn-primary" style={{ padding: '16px 28px', fontSize: 15, textDecoration: 'none' }}>
                  Start your free 30 days <Icon name="arrow" size={16} />
                </Link>
                <a href="#how" className="btn btn-ghost" style={{ padding: '16px 24px', fontSize: 15, textDecoration: 'none' }}>
                  See how it works
                </a>
              </div>
              <div style={{ marginTop: 36, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex' }}>
                  {[
                    { c: '#7dd3a8', i: 'SM' },
                    { c: '#e0a87a', i: 'JP' },
                    { c: '#b394d4', i: 'DK' },
                    { c: '#7faed4', i: 'RT' },
                  ].map((a, idx) => (
                    <div key={a.i} style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: a.c, marginLeft: idx ? -8 : 0,
                      border: '2px solid var(--bg-deep)',
                      fontSize: 11, fontWeight: 700, color: 'rgba(0,0,0,.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{a.i}</div>
                  ))}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.4 }}>
                  <div style={{ color: 'var(--accent)', fontWeight: 600 }}>★★★★★ 4.9</div>
                  Loved by 12,000+ families
                </div>
              </div>
            </div>

            <div style={{ position: 'relative', marginTop: 32 }} className="md:mt-0">
              <div style={{
                position: 'absolute', top: -20, left: -20, right: -20, bottom: -20,
                background: 'radial-gradient(circle at 50% 40%, var(--accent-soft) 0%, transparent 70%)',
                filter: 'blur(40px)',
              }} />
              <AnimatedGrid />
              <div className="hidden sm:flex" style={{
                position: 'absolute', top: -14, right: -16,
                background: 'var(--surface-2)', border: '1px solid var(--line)',
                padding: '8px 14px', borderRadius: 'var(--r-pill)',
                fontSize: 12, fontWeight: 600, color: 'var(--ink)',
                boxShadow: 'var(--shadow-pop)',
                alignItems: 'center', gap: 8,
                transform: 'rotate(-3deg)',
              }}>
                <Icon name="leaf" size={14} style={{ color: 'var(--accent)' }} />
                Vegetarian-friendly
              </div>
              <div className="hidden sm:flex" style={{
                position: 'absolute', bottom: 80, left: -28,
                background: 'var(--surface-2)', border: '1px solid var(--line)',
                padding: '8px 14px', borderRadius: 'var(--r-pill)',
                fontSize: 12, fontWeight: 600, color: 'var(--ink)',
                boxShadow: 'var(--shadow-pop)',
                alignItems: 'center', gap: 8,
                transform: 'rotate(2deg)',
              }}>
                <Icon name="wallet" size={14} style={{ color: 'var(--accent)' }} />
                Under $200
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust band ────────────────────────────────────────────────────── */}
      <section style={{
        padding: '20px 24px',
        borderTop: '1px solid var(--line-soft)',
        borderBottom: '1px solid var(--line-soft)',
        background: 'var(--bg-deep)',
        fontSize: 13, color: 'var(--ink-3)',
      }}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-around gap-y-3 gap-x-6">
          <div>Loved by <strong style={{ color: 'var(--ink)' }}>12,000+ families</strong></div>
          <div className="hidden md:block" style={{ width: 1, height: 18, background: 'var(--line)' }} />
          <div>Save up to <strong style={{ color: 'var(--ink)' }}>$240/month</strong> on groceries</div>
          <div className="hidden md:block" style={{ width: 1, height: 18, background: 'var(--line)' }} />
          <div><strong style={{ color: 'var(--ink)' }}>27% less</strong> food waste</div>
          <div className="hidden md:block" style={{ width: 1, height: 18, background: 'var(--line)' }} />
          <div><strong style={{ color: 'var(--ink)' }}>4.9</strong> avg rating</div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <section id="how" style={{ padding: '80px 24px' }}>
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            eyebrow="How it works"
            title={<>Three steps to<br/>a calmer kitchen.</>}
            sub="Tell us about your family. We do the rest — every week, automatically."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ marginTop: 40 }}>
            {STEPS.map((s) => (
              <div key={s.n} className="card" style={{ padding: 32, position: 'relative' }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--ink-4)',
                  marginBottom: 24,
                  letterSpacing: '0.15em',
                }}>{s.n} / 03</div>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: 'var(--accent-soft)', color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20,
                }}>
                  <Icon name={s.icon} size={22} />
                </div>
                <h3 className="h-serif" style={{ fontSize: 22, margin: '0 0 10px', color: 'var(--ink)' }}>{s.t}</h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink-3)', margin: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Backed by science ─────────────────────────────────────────────── */}
      <section style={{ padding: '0 24px 80px' }}>
        <div className="max-w-6xl mx-auto">
          <div className="card" style={{
            padding: 'clamp(32px, 5vw, 56px) clamp(24px, 4vw, 48px)',
            background: 'var(--surface-2)',
            backgroundImage: 'radial-gradient(circle at 80% 0%, var(--accent-soft) 0%, transparent 50%)',
          }}>
            <SectionTitle
              eyebrow="Backed by science"
              title="Meal planning works."
              sub="The benefits aren't anecdotal. Here's what the research shows."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" style={{ marginTop: 32 }}>
              {SCIENCE.map((s, i) => (
                <div key={s.stat} className={i ? 'md:pl-8 md:border-l' : ''} style={i ? { borderColor: 'var(--line-soft)' } : undefined}>
                  <div className="h-display" style={{ fontSize: 'clamp(44px, 6vw, 64px)', color: 'var(--accent)', lineHeight: 1, marginBottom: 8 }}>{s.stat}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginBottom: 12 }}>{s.label}</div>
                  <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-3)', margin: '0 0 12px' }}>{s.body}</p>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-4)', letterSpacing: '0.05em' }}>{s.src}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features grid ─────────────────────────────────────────────────── */}
      <section id="features" style={{ padding: '0 24px 80px' }}>
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            eyebrow="Features"
            title="Everything your family needs."
            sub="From planning to shopping, Platterplan handles the details so you don't have to."
          />
          <div
            className="grid gap-5"
            style={{ marginTop: 40, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
          >
            <div className="card md:row-span-2" style={{ padding: 32, display: 'flex', flexDirection: 'column', gridColumn: 'span 1' }}>
              <Placeholder tone="warm" label="hero · roast & vegetables" style={{ height: 220, marginBottom: 24 }} />
              <h3 className="h-serif" style={{ fontSize: 26, margin: '0 0 12px', color: 'var(--ink)' }}>AI-powered weekly plans</h3>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ink-3)', margin: 0 }}>
                Tell us what you like and we build a complete 7-day plan — breakfast, lunch, and dinner — tailored
                to your family in seconds.
              </p>
              <div style={{ marginTop: 'auto', paddingTop: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['family roster', 'budget-aware', 'skill level', 'busy nights'].map(t => (
                  <Chip key={t} size="sm">{t}</Chip>
                ))}
              </div>
            </div>
            {SMALL_FEATURES.map((f) => (
              <div key={f.t} className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Placeholder tone={f.tone} label={f.t.toLowerCase()} style={{ height: 100 }} />
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Icon name={f.icon} size={16} style={{ color: 'var(--accent)' }} />
                    <h3 style={{ fontSize: 16, margin: 0, color: 'var(--ink)', fontWeight: 600 }}>{f.t}</h3>
                  </div>
                  <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-3)', margin: 0 }}>{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ──────────────────────────────────────────────────── */}
      <section id="testimonials" style={{ padding: '0 24px 80px' }}>
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            eyebrow="Real families"
            title="It feels like a Sunday off."
            sub="Here's what our members are saying after 90 days."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ marginTop: 40 }}>
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="card" style={{ padding: 28, display: 'flex', flexDirection: 'column' }}>
                <div style={{ color: 'var(--accent)', marginBottom: 16, fontSize: 14 }}>★★★★★</div>
                <p className="h-serif" style={{ fontSize: 18, lineHeight: 1.5, color: 'var(--ink)', margin: '0 0 24px', fontStyle: 'italic', flex: 1 }}>
                  “{t.quote}”
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 20, borderTop: '1px solid var(--line-soft)' }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: t.color, color: 'rgba(0,0,0,.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13,
                  }}>
                    {t.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{t.loc} · {t.days}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────────────── */}
      <section id="pricing" style={{ padding: '0 24px 80px' }}>
        <div className="max-w-6xl mx-auto">
          <div className="card grid grid-cols-1 md:grid-cols-[1.1fr_1fr]" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: 'clamp(32px, 5vw, 56px) clamp(24px, 4vw, 48px)' }}>
              <TrialPill>30 days free, then $9.99/mo</TrialPill>
              <h2 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: '20px 0 16px', color: 'var(--ink)', lineHeight: 1.02 }}>
                One price.<br/>No tiers.
              </h2>
              <p style={{ fontSize: 16, color: 'var(--ink-3)', lineHeight: 1.55, maxWidth: 380, margin: '0 0 32px' }}>
                Try everything free for 30 days. Cancel any time. No credit card needed to start.
              </p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 28 }}>
                <span className="h-display" style={{ fontSize: 'clamp(48px, 8vw, 72px)', color: 'var(--ink)' }}>$9.99</span>
                <span style={{ color: 'var(--ink-3)', fontSize: 15 }}>/ month</span>
              </div>
              <Link to="/signup" className="btn btn-primary" style={{ padding: '16px 32px', fontSize: 15, width: 'fit-content', textDecoration: 'none' }}>
                Start your free 30 days <Icon name="arrow" size={16} />
              </Link>
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink-4)' }}>
                No card needed · Cancel any time
              </div>
            </div>
            <div style={{
              padding: 'clamp(32px, 5vw, 56px) clamp(24px, 4vw, 48px)',
              background: 'var(--surface-2)',
              borderLeft: '1px solid var(--line-soft)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 24 }}>
                Everything included
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {PLAN_FEATURES.map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: 'var(--ink-2)' }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: 'var(--accent-soft)', color: 'var(--accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon name="check" size={12} stroke={2.4} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section style={{ padding: '0 24px 80px' }}>
        <div className="max-w-6xl mx-auto">
          <div style={{
            padding: 'clamp(48px, 8vw, 80px) clamp(24px, 4vw, 48px)',
            background: 'var(--accent)', color: 'var(--accent-ink)',
            borderRadius: 'var(--r-xl)',
            textAlign: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'var(--grain)', backgroundSize: '160px 160px', pointerEvents: 'none' }} />
            <h2 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 64px)', lineHeight: 1.0, margin: '0 0 16px', position: 'relative' }}>
              Your best meals are<br/>one click away.
            </h2>
            <p style={{ fontSize: 17, opacity: 0.8, maxWidth: 480, margin: '0 auto 32px', position: 'relative' }}>
              Join thousands of families who've taken the stress out of weeknight dinners.
              Your first month is completely free.
            </p>
            <Link to="/signup" style={{
              background: 'var(--accent-ink)', color: 'var(--accent)',
              padding: '18px 36px', borderRadius: 'var(--r-pill)',
              fontSize: 16, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 10,
              position: 'relative', textDecoration: 'none',
            }}>
              Start your free 30 days <Icon name="arrow" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer style={{
        padding: '32px 24px',
        borderTop: '1px solid var(--line-soft)',
        fontSize: 13, color: 'var(--ink-4)',
      }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <Logo size={15} />
          <span>© {new Date().getFullYear()} Platterplan · All rights reserved</span>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</a>
            <a href="mailto:support@platterplan.com" style={{ color: 'inherit', textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
